/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { IClientDto, IClientFilter } from '../../client/Client.model'
import { useClientGetByIdMutation, useClientSearchMutation } from '../../client/Client.api'
import { IFilterDto } from '../../../common/model/Core.model'
import { FieldArray, FormikProvider, useFormik } from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import { ClientListItem } from '../../client/components/ClientListItem'
import { IAssetDto, IAssetFilter } from '../../asset/Asset.model'
import { useAssetGetByIdMutation, useAssetSearchMutation } from '../../asset/Asset.api'
import { AssetListItem } from '../../asset/components/AssetListItem'
import { useReportMetadataGetByAreaMutation } from '../../report-metadata/ReportMetadata.api'
import { IReportCreateRequestDto, IReportStatus, IReportWorkOrderRequestDto, IWorkOrderLineType } from '../Report.model'
import { useReportCreateMutation } from '../Report.api'
import { IMetadata, IReportMetadataDto } from '../../report-metadata/ReportMetadata.model'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { TagInputSearchListControls } from '../../../common/component/TagInputSearchListControls'
import '../../../common/css/SearchControls.css'
import classNames from 'classnames'
import { ReportRenderer } from '../../../common/component/ReportRenderer'
import { useAppSelector } from '../../store/Store'
import { array, boolean, object, string } from 'yup'
import { COUNTRIES_EN, FREQUENCIES, FREQUENCY } from '../../../util/Constants'
import { selectUser } from '../../profile/Profile.slice'
import { IContractDto, IContractFilter } from '../../contract/Contract.model'
import { useContractGetByIdMutation } from '../../contract/Contract.api'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { IAttachment } from '../../media/Media.model'
import { FilePicker } from '../../../common/component/FilePicker'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { ContactDetailsType, IContactDto, IContactFilter } from '../../contact/Contact.model'
import ReportMetadataFormValidator from '../../report-metadata/ReportMetadataFormValidator.util'
import { ContactEditor } from '../../contact/components/ContactEditor'
import { DateTimeInputPicker } from '../../../common/component/DateTimeInputPicker'
import { useContactSearchMutation } from '../../contact/Contact.api'
import { ContractEmbedered } from '../../contract/components/ContractEmbedered'
import { get } from 'lodash';
import { PriorityLevel, PriorityLevelOptions } from '../../helpdesk/ticket/Ticket.model'

interface ICreateFormikSchema {
  clientId: string
  assetId: string
  reportMetadataId: string
  contractId?: string
  status: string
  serviceDesk: boolean
  startedAt?: string
  endedAt?: string
  countryCode?: string
  country?: string
  address: string
  latitude?: string
  longitude?: string
  visitType: string
  category: string
  frequencyCode: string
  frequency: string
  contacts: IContactDto[]
  technicians: IContactDto[]
  finalVersion: boolean
  metadata: IMetadata
  attachments: IAttachment[]
  workOrderRequest?: IReportWorkOrderRequestDto
}

const ReportNewPage = () => { 
  const intl = useIntl()
  const formValidator = new ReportMetadataFormValidator(intl);
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search)
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.REPORTS'}),
      path: '/reports',
      isSeparator: false,
      isActive: false,
    },
  ]

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [visitTypes, setVisitTypes] = useState(['CONTRATO', 'GARANTIA', 'PUNTUAL'])
  const [serviceCategories, setServiceCategories] = useState([
    'CORRECTIVO',
    'INSTALACIÓN',
    'MANTENIMIENTO PREVENTIVO',
    'MANTENIMIENTO MAYOR',
    'REVISIÓN Y DIAGNOSTIGO',
  ])
  const [validationSchema, setValidationSchema] = useState<any>(
    object().shape({
      clientId: formValidator.validateStringStrict(),
      assetId: formValidator.validateStringStrict(),
      reportMetadataId: formValidator.validateStringStrict(),
      reportMetadataSequence: formValidator.validateStringStrict(),
      contractId: formValidator.validateString(),
      status: formValidator.validateStringStrict(),
      startedAt: formValidator.validateInputDate(),
      endedAt: formValidator.validateInputDate(),
      visitType: formValidator.validateStringStrict(),
      category: formValidator.validateStringStrict(),
      frequencyCode: formValidator.validateStringStrict(),
      frequency: string().when('frequencyCode', {
        is: (value) => value === FREQUENCY.OTHER,
        then: () => formValidator.validateStringStrict(),
      }),
      countryCode: formValidator.validateString(),
      address: formValidator.validateStringStrict(),
      latitude: formValidator.validateString(),
      longitude: formValidator.validateString(),
      contacts: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      technicians: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      finalVersion: boolean().required(),
      metadata: object().when('finalVersion', {
        is: true,
        then: () => formValidator.validateStrict(),
        otherwise: () => formValidator.validate(),
      }),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
      workOrderRequest: object().shape({
        lines: array()
          .of(
            object().shape({
              workOrderTypeCode: formValidator.validateString(),
              details: string().when('workOrderTypeCode', {
                is: (value) => !!value,
                then: () => formValidator.validateStringStrict(),
                otherwise: () => formValidator.validateString(),
              }),
            })
          )
          .when('attachments', {
            is: (attachments) => Array.isArray(attachments) && attachments.length > 0,
            then: (schema) =>
              schema
                .min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1}))
                .test(
                  'work-order-type-required-when-attachments',
                  intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}),
                  (lines) =>
                    Array.isArray(lines) && lines.every((line) => !!line?.workOrderTypeCode)
                ),
          }),
        priorityCode: string().when(['lines', 'attachments'], {
          is: (lines, attachments) =>
            (Array.isArray(attachments) && attachments.length > 0) ||
            (Array.isArray(lines) && lines.some((line) => !!line?.workOrderTypeCode)),
          then: () => formValidator.validateStringStrict(),
          otherwise: () => formValidator.validateString(),
        }),
      }),
    })
  )

  const [initialValues, setInitialValues] = useState<ICreateFormikSchema>({
    clientId: '',
    assetId: '',
    reportMetadataId: '',
    contractId: '',
    status: IReportStatus.COMPLETED,
    serviceDesk: false,
    startedAt: new Date().toISOString(),
    visitType: '',
    frequencyCode: '',
    frequency: '',
    category: '',
    address: '',
    countryCode: '',
    country: '',
    latitude: '',
    longitude: '',
    contacts: [],
    technicians: [],
    finalVersion: true,
    metadata: {
      items: []
    },
    attachments: [],
    workOrderRequest: {
      reportedBy: '',
      priorityCode: undefined,
      priority: undefined,
      lines: [],
      attachments: [],
    },
  })

  const clientFilterKeys = {
    defaultKey: IClientFilter.NAME,
    defaultKeyList: [
      IClientFilter.ID,
      IClientFilter.NAME,
      IClientFilter.SECTOR,
      IClientFilter.ADDRESS,
    ],
  }
  const didFetchClient = useRef(false)
  const [clientFilterSuggestions, setClientFilterSuggestions] = useState<string[]>(clientFilterKeys.defaultKeyList)
  const [clientFilters, setClientFilters] = useState<IFilterDto[]>([])
  const [client, setClient] = useState<IClientDto | undefined>()
  const [isClientLoading, setIsClientLoading] = useState(false)
  const [clientSearchResult, setClientSearchResult] = useState<IClientDto[] | undefined>(undefined)
  const [clientGetById] = useClientGetByIdMutation()
  const [clientSearch] = useClientSearchMutation()

  const assetFilterKeys = {
    defaultKey: IAssetFilter.CLIENT,
    defaultKeyList: [
      IAssetFilter.ID,
      IAssetFilter.FAMILY,
      IAssetFilter.AREA,
      IAssetFilter.BRAND,
      IAssetFilter.MODEL,
      IAssetFilter.SERIAL,
      IAssetFilter.ADDRESS,
    ],
  }
  const didFetchAsset = useRef(false)
  const [assetFilterSuggestions, setAssetFilterSuggestions] = useState<string[]>(assetFilterKeys.defaultKeyList)
  const [assetFilters, setAssetFilters] = useState<IFilterDto[]>([])
  const [asset, setAsset] = useState<IAssetDto | undefined>(undefined)
  const [isAssetLoading, setIsAssetLoading] = useState(false)
  const [assetSearchResult, setAssetSearchResult] = useState<IAssetDto[] | undefined>(undefined)
  const [assetGetById] = useAssetGetByIdMutation()
  const [assetSearch] = useAssetSearchMutation()

  const didFetchContract = useRef(false)
  const [isContractLoading, setIsContractLoading] = useState(false)
  const [contract, setContract] = useState<IContractDto>()
  const [contractGetById] = useContractGetByIdMutation()
  
  const didFetchReportMetadata = useRef(false)
  const [reportMetadata, setReportMetadata] = useState<IReportMetadataDto | undefined>(undefined)
  const [isReportMetadataLoading, setIsReportMetadataLoading] = useState(false)
  const [reportMetadataGetByArea] = useReportMetadataGetByAreaMutation()

  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)
  
  const [reportCreate] = useReportCreateMutation()

  const user = useAppSelector(selectUser)

  const [contactSearch] = useContactSearchMutation()
  const didFetchTecnician = useRef(false)
  const [isDefaultTechLoading, setIsDefaultTechLoading] = useState(false)
  const formik = useFormik<ICreateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: IReportCreateRequestDto = {
            clientId: values.clientId,
            assetId: values.assetId,
            reportMetadataId: values.reportMetadataId,
            contractId: values.contractId,
            status: values.finalVersion ? IReportStatus.COMPLETED : IReportStatus.PENDING,
            serviceDesk: values.serviceDesk,
            startedAt: values.startedAt,
            endedAt: values.endedAt,
            countryCode: values.countryCode,
            country: intl.formatMessage({id: `DROPDOWN.COUNTRY.${values.countryCode}`}),
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            visitType: values.visitType,
            category: values.category,
            frequencyCode: values.frequencyCode,
            frequency:
              values.frequencyCode === FREQUENCY.OTHER
                ? values.frequency.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.FREQUENCY.${values.frequencyCode}`}),
            contacts: values.contacts,
            technicians: values.technicians,
            metadata: values.metadata,
            attachments: values.attachments,
          }

          const workOrderRequest: IReportWorkOrderRequestDto | undefined = values.workOrderRequest
          if (
            workOrderRequest &&
            workOrderRequest.lines &&
            workOrderRequest.lines.length > 0
          ) {
            workOrderRequest.lines.forEach((line) => {
              line.workOrderType = intl.formatMessage({
                id: `${`WORK_ORDER_LINE_TYPE.${line.workOrderTypeCode}`.toUpperCase()}`,
              })
            })
            workOrderRequest.priority = intl.formatMessage({
              id: `TICKET.PRIORITY.${workOrderRequest.priorityCode || PriorityLevel.LOW}`,
            })
          }
          request.workOrderRequest = workOrderRequest
        
          const response: any = await reportCreate(request).unwrap()
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/reports/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setClientSearchResult(undefined)
        setAssetSearchResult(undefined)
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchClient = async (clientId) => {
      try {
        if (!didFetchClient.current) {
          setIsClientLoading(true)
          const response: any = await clientGetById({id: clientId, fields: 'LIMITED'})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const client: IClientDto = response.data
            setClient(client)
            setClientSearchResult(undefined)

            formik.setFieldValue('clientId', client.id)
            formik.setFieldValue('contacts', [
              ...client.contacts.filter((contact) => contact.primary),
              ...formik.values.contacts,
            ])

            setAssetFilters([{id: IContractFilter.CLIENT_HASH, value: client.id, editable: false}])
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsClientLoading(false)
      }
    }

    const fetchAsset = async (assetId) => {
      try {
        if (!didFetchAsset.current) {
          setIsAssetLoading(true)
          const response: any = await assetGetById({id: assetId, fields: 'LIMITED'})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const asset: IAssetDto = response.data
            setAsset(asset)
            setAssetSearchResult(undefined)

            formik.setFieldValue('assetId', asset.id)
            formik.setFieldValue('countryCode', asset.countryCode)
            formik.setFieldValue('address', asset.address)

            fetchClient(asset.client.id)
            fetchReportMedatada(asset.familyCode, asset.areaCode)
            if (asset.contract) {
              fetchContract(asset.contract.id)
            }
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAssetLoading(false)
      }
    }

    const clientId = queryParams.get('clientId')
    const assetId = queryParams.get('assetId')
    if (assetId) {
      fetchAsset(assetId)
    } else if (clientId) {
      fetchClient(clientId)
    }
  }, [])

  useEffect(() => {
    const fetchDefaultTechnician = async (email: string) => {
      try {
        setIsDefaultTechLoading(true)
        if (!didFetchTecnician.current) {
          const response: any = await contactSearch({
            filters: [{id: IContactFilter.EMAIL, value: email, editable: false}],
          })
         if (!response || response.error) {
           setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
         } else {
           const technicians: IContactDto[] = response.data.data           
           formik.setFieldValue('technicians', [
             ...technicians.map((contact) => {
              const defaultContact: IContactDto = {
                createdAt: contact.createdAt,
                updatedAt: contact.updatedAt,
                organizationId: contact.organizationId,
                userId: contact.userId,
                primary: true,
                id: contact.id,
                sequence: contact.sequence,
                type: contact.type,
                displayName: contact.displayName,
                email: contact.email,
                telephone: contact.telephone,
                countryCode: contact.countryCode,
                country: contact.country,
                address: contact.address,
                details: contact.details,
                photoUrl: contact.photoUrl,
              }
              return defaultContact;
             }),
           ])

           const defaultTechnician =
             technicians && technicians.length > 0 ? technicians[0] : undefined
           if (defaultTechnician) {
             formik.setFieldValue('workOrderRequest.reportedBy', defaultTechnician.id)
           }
         }
        }
      } finally {
        setIsDefaultTechLoading(false)
      }
    }

    if (user) {
      fetchDefaultTechnician(user.email)
    }
  }, [user])

  const fetchReportMedatada = async (familyCode, areaCode) => {
    try {
      if (!didFetchReportMetadata.current) {
        setIsReportMetadataLoading(true)
        const response: any = await reportMetadataGetByArea({familyCode, areaCode})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          const reportMetadata: IReportMetadataDto = response.data
          setReportMetadata(reportMetadata)

          formik.setFieldValue('reportMetadataId', reportMetadata.id)
          formik.setFieldValue('reportMetadataSequence', reportMetadata.sequence)
          formik.setFieldValue('metadata', reportMetadata.metadata)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsReportMetadataLoading(false)
    }
  }

  const fetchContract = async (contractId) => {
    try {
      if (!didFetchContract.current) {
        setIsContractLoading(true)
        const response: any = await contractGetById({id: contractId, fields: 'LIMITED'})
        if (response && response.data) {
          const contract: IContractDto = response.data
          setContract(contract)

          formik.setFieldValue('contractId', contract.id)
          formik.setFieldValue('visitType', contract.documentType)
          formik.setFieldValue('frequencyCode', contract.serviceFrequencyCode)
          formik.setFieldValue(
            'frequency',
            contract.serviceFrequencyCode === FREQUENCY.OTHER
              ? contract.serviceFrequency
              : intl.formatMessage({id: `DROPDOWN.FREQUENCY.${contract.serviceFrequencyCode}`})
          )
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsContractLoading(false)
    }
  }

  const clientRowOnClickHandler = async (id) => {
    if (clientSearchResult) {
      const client = clientSearchResult.find((client) => client.id === id)
      if (client) {
        setClient(client)
        setClientSearchResult(undefined)

        formik.setFieldValue('clientId', client.id)
        formik.setFieldValue('contacts', [
          ...client.contacts.filter((contact) => contact.primary),
          ...formik.values.contacts,
        ])

        setAssetFilters([{id: IContractFilter.CLIENT_HASH, value: client.id, editable: false}])
      }
    }
  }

  const clientRemoveOnClickHandler = async () => {
    setClient(undefined)
    setClientSearchResult(undefined)
    setClientFilters([])
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

    setAsset(undefined)
    setAssetSearchResult(undefined)
    setAssetFilters([])
    setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)

    setContract(undefined)
    setReportMetadata(undefined)

    formik.setFieldValue('clientId', '')
    formik.setFieldValue('assetId', '')
    formik.setFieldValue('countryCode', '')
    formik.setFieldValue('address', '')
    formik.setFieldValue('contacts', [
      ...formik.values.contacts.filter((contact) => !contact.primary),
    ])
  }

  const clientSearchOnClickHandler = async () => {
    try {
      if (!didFetchClient.current) {
        setIsClientLoading(true)
        const response: any = await clientSearch({filters: clientFilters})
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setClientSearchResult(response.data.data)
          setClientFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsClientLoading(false)
    }
  }

  const clientResetSearchResetOnClickHandler = async () => {
    setClientSearchResult(undefined)
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)
  }

  const assetRowOnClickHandler = async (assetId) => {
    if (assetSearchResult) {
      const asset = assetSearchResult.find((asset) => asset.id === assetId)
      if (asset) {
        setAsset(asset)
        setAssetSearchResult(undefined)

        formik.setFieldValue('assetId', asset.id)
        formik.setFieldValue('countryCode', asset.countryCode)
        formik.setFieldValue('address', asset.address)
        
        fetchReportMedatada(asset.familyCode, asset.areaCode)
        if (asset.contract) {
          fetchContract(asset.contract.id)
        }
      }
    }
  }

  const assetRemoveOnClickHandler = async () => {
    setAsset(undefined)
    setAssetSearchResult(undefined)
    setReportMetadata(undefined)
    setContract(undefined)

    if (client) {
      setAssetFilters([{id: IAssetFilter.CLIENT_HASH, value: client.id, editable: false}])
      setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)
    }

    formik.setFieldValue('assetId', '')
    formik.setFieldValue('reportMetadataId', '')
    formik.setFieldValue('contractId', '')
    formik.setFieldValue('countryCode', '')
    formik.setFieldValue('address', '')
    formik.setFieldValue('metadata', '')
  }

  const assetSearchOnClickHandler = async () => {
    try {
      if (client && !didFetchAsset.current) {
        setIsAssetLoading(true)
        const response: any = await assetSearch({filters: assetFilters})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setAssetSearchResult(response.data.data)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsAssetLoading(false)
    }
  }

  const assetResetSearchResetOnClickHandler = async () => {
    setAssetFilterSuggestions(assetFilterKeys.defaultKeyList)
    setAssetSearchResult(undefined)
  }

  const removeDetail = (index) => {
    formik.setFieldValue(
      `workOrderRequest.lines`,
      get(formik.values, `workOrderRequest.lines`).filter((_, i) => i !== index)
    )
  }

  const addDetail = () => {
    const workOrderRequest: IReportWorkOrderRequestDto = get(formik.values, 'workOrderRequest')
    formik.setFieldValue('workOrderRequest.lines', [
      ...workOrderRequest.lines,
      {
        workOrderTypeCode: IWorkOrderLineType.SPARE_PART,
        workOrderType: IWorkOrderLineType.SPARE_PART,
        details: '',
      },
    ])
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.clientId && formik.errors.clientId) ||
          (formik.touched.assetId && formik.errors.assetId) ||
          (formik.touched.reportMetadataId && formik.errors.reportMetadataId) ||
          (formik.touched.status && formik.errors.status) ||
          (formik.touched.startedAt && formik.errors.startedAt) ||
          (formik.touched.endedAt && formik.errors.endedAt) ||
          (formik.touched.visitType && formik.errors.visitType) ||
          (formik.touched.category && formik.errors.category) ||
          (formik.touched.frequencyCode && formik.errors.frequencyCode) ||
          (formik.touched.frequency && formik.errors.frequency) ||
          (formik.touched.countryCode && formik.errors.countryCode) ||
          (formik.touched.address && formik.errors.address) ||
          (formik.touched.latitude && formik.errors.latitude) ||
          (formik.touched.longitude && formik.errors.longitude)
        )
      }
      case 'kt_tab_pane_2': {
        return formik.touched.contacts && formik.errors.contacts
      }
      case 'kt_tab_pane_3': {
        return formik.touched.technicians && formik.errors.technicians
      }
      case 'kt_tab_pane_4': {
        return formik.touched.metadata && formik.errors.metadata
      }
      case 'kt_tab_pane_5': {
        return formik.touched.attachments && formik.errors.attachments
      }
      case 'kt_tab_pane_6': {
        const hasWorkOrderErrors = !!get(formik.errors, 'workOrderRequest')
        const workOrderTouched = !!get(formik.touched, 'workOrderRequest')
        return hasWorkOrderErrors && (workOrderTouched || formik.submitCount > 0)
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>
                          {intl.formatMessage({id: 'UTIL.CREATE.REPORT'})}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary me-2 d-none d-md-block'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-floppy'></i>
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'UTIL.SAVE'})}
                            </span>
                          </>
                        )}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-floppy'></i>
                        )}
                      </button>

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/reports`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {tabWithError('kt_tab_pane_1') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {tabWithError('kt_tab_pane_2') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                      {tabWithError('kt_tab_pane_3') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.TECHNICIANS'})}
                    </a>
                  </li>

                  {reportMetadata && reportMetadata.metadata && (
                    <>
                      <li className='nav-item'>
                        <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                          {tabWithError('kt_tab_pane_4') && (
                            <span className='text-danger me-1'>*</span>
                          )}
                          {intl.formatMessage({id: 'UTIL.REPORT'})}
                        </a>
                      </li>
                      <li className='nav-item'>
                        <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_5'>
                          {tabWithError('kt_tab_pane_5') && (
                            <span className='text-danger me-1'>*</span>
                          )}
                          {intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                        </a>
                      </li>
                      {/* HIDDEN SPARE PARTS FEATURE TAB FOR NOW */}
                      {/* <li className='nav-item'>
                        <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_6'>
                          {tabWithError('kt_tab_pane_6') && (
                            <span className='text-danger me-1'>*</span>
                          )}
                          {intl.formatMessage({id: 'UTIL.SPARE_PARTS'})}
                        </a>
                      </li> */}
                    </>
                  )}
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      {!client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span
                              className={classNames('required', {
                                'text-gray-700': !(
                                  formik?.touched?.clientId && formik?.errors?.clientId
                                ),
                                'text-danger':
                                  formik?.touched?.clientId && formik?.errors?.clientId,
                              })}
                            >
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <TagInputSearchListControls
                              loading={isClientLoading || isAssetLoading}
                              enabled={!formik.isSubmitting}
                              filterKeys={clientFilterKeys}
                              filterSuggestions={clientFilterSuggestions}
                              filters={clientFilters}
                              createOnClickPath={'/clients/new'}
                              setFilterSuggestions={setClientFilterSuggestions}
                              setFilters={setClientFilters}
                              searchOnClickHandler={clientSearchOnClickHandler}
                              resetSearchResetOnClickHandler={clientResetSearchResetOnClickHandler}
                            ></TagInputSearchListControls>
                          </div>
                          <label className='col-xxl-3'></label>
                          <div className='col-xxl-9 h-3px'>
                            {clientSearchResult && clientSearchResult?.length > 0 && (
                              <ul
                                id='report-client-autocomplete-list'
                                className='suggestions-list'
                                role='listbox'
                              >
                                {clientSearchResult.map((item, index) => (
                                  <li key={index} onClick={() => clientRowOnClickHandler(item.id)}>
                                    <ClientListItem data={item}></ClientListItem>
                                    <div className='separator'></div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </>
                      )}

                      {client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span className='text-gray-700 required'>
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <ClientListItem
                              disabled={formik.isSubmitting}
                              data={client}
                              rowSelected={client.id}
                              removeOnClick={clientRemoveOnClickHandler}
                            ></ClientListItem>
                          </div>
                        </>
                      )}
                    </div>

                    {client && (
                      <>
                        <div className='row mb-5'>
                          {!asset && (
                            <>
                              <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                <span
                                  className={classNames('', {
                                    'text-gray-700': !(
                                      formik?.touched?.assetId && formik?.errors?.assetId
                                    ),
                                    'text-danger':
                                      formik?.touched?.assetId && formik?.errors?.assetId,
                                  })}
                                >
                                  {intl.formatMessage({id: 'UTIL.ASSET'})}
                                </span>
                              </label>

                              <div className='col-xxl-9'>
                                <TagInputSearchListControls
                                  loading={isAssetLoading || isClientLoading}
                                  enabled={!formik.isSubmitting}
                                  filterKeys={assetFilterKeys}
                                  filterSuggestions={assetFilterSuggestions}
                                  filters={assetFilters}
                                  createOnClickPath={`/assets/new?clientId=${client.id}`}
                                  setFilterSuggestions={setAssetFilterSuggestions}
                                  setFilters={setAssetFilters}
                                  searchOnClickHandler={assetSearchOnClickHandler}
                                  resetSearchResetOnClickHandler={
                                    assetResetSearchResetOnClickHandler
                                  }
                                ></TagInputSearchListControls>
                              </div>
                              <label className='col-xxl-3'></label>
                              <div className='col-xxl-9 h-3px'>
                                {assetSearchResult && assetSearchResult.length > 0 && (
                                  <ul
                                    id='autocomplete-list'
                                    className='suggestions-list'
                                    role='listbox'
                                  >
                                    {assetSearchResult.map((asset, index) => (
                                      <li
                                        key={index}
                                        onClick={() => assetRowOnClickHandler(asset.id)}
                                      >
                                        <AssetListItem
                                          data={asset}
                                          rowSelected={asset.id}
                                        ></AssetListItem>
                                        <div className='separator'></div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </>
                          )}

                          {asset && (
                            <>
                              <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                <span className='text-gray-700'>
                                  {intl.formatMessage({id: 'UTIL.ASSET'})}
                                </span>
                              </label>

                              <div className='col-xxl-9'>
                                <AssetListItem
                                  disabled={formik.isSubmitting}
                                  data={asset}
                                  rowSelected={asset.id}
                                  removeOnClick={assetRemoveOnClickHandler}
                                ></AssetListItem>
                              </div>
                            </>
                          )}
                        </div>

                        {asset && asset.contract && <ContractEmbedered contract={asset.contract} />}
                      </>
                    )}

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_DESK'})}
                            disabled={formik.isSubmitting}
                            object={
                              <div className='form-check form-switch form-check-custom form-check-solid ps-3'>
                                <input
                                  id='draftSwitchChecked'
                                  className='form-check-input h-25px w-35px'
                                  type='checkbox'
                                  {...formik.getFieldProps(`serviceDesk`)}
                                  onChange={formik.handleChange}
                                  checked={formik.values.serviceDesk}
                                  disabled={formik.isSubmitting}
                                />
                              </div>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.FINAL_VERSION'})}
                            disabled={formik.isSubmitting}
                            object={
                              <div className='form-check form-switch form-check-custom form-check-solid ps-3'>
                                <input
                                  id='draftSwitchChecked'
                                  className='form-check-input h-25px w-35px'
                                  type='checkbox'
                                  {...formik.getFieldProps(`finalVersion`)}
                                  onChange={formik.handleChange}
                                  checked={formik.values.finalVersion}
                                  disabled={formik.isSubmitting}
                                />
                              </div>
                            }
                          ></CustomInputRenderer>

                          <DateTimeInputPicker
                            label={intl.formatMessage({id: 'UTIL.DATE_TIME_START'})}
                            placeholder={intl.formatMessage({id: 'UTIL.DATE_TIME_START'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='startedAt'
                            selectOnClickHandler={(date) => {
                              if (formik.values.endedAt) {
                                const endedAt = new Date(formik.values.endedAt)
                                if (date > endedAt) {
                                  formik.setFieldValue('endedAt', '')
                                }
                              }
                            }}
                          ></DateTimeInputPicker>

                          <DateTimeInputPicker
                            label={intl.formatMessage({id: 'UTIL.DATE_TIME_END'})}
                            placeholder={intl.formatMessage({id: 'UTIL.DATE_TIME_END'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='endedAt'
                            minDateEnable={!!formik.values.startedAt}
                            minDate={((): string | undefined => {
                              if (formik.values.startedAt) {
                                const startedAt = new Date(formik.values.startedAt)
                                return new Date(
                                  startedAt.getFullYear(),
                                  startedAt.getMonth(),
                                  startedAt.getDate(),
                                  startedAt.getHours(),
                                  startedAt.getMinutes() + 1,
                                  0
                                ).toUTCString()
                              }
                              return undefined
                            })()}
                            selectOnClickHandler={(date) => {
                              if (formik.values.startedAt) {
                                const startedAt = new Date(formik.values.startedAt)
                                if (startedAt > date) {
                                  formik.setFieldValue('endedAt', '')
                                }
                              }
                            }}
                          ></DateTimeInputPicker>

                          {contract ? (
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.VISIT_TYPE'})}
                              value={`${contract.documentType}`}
                            ></CustomInputRenderer>
                          ) : (
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.VISIT_TYPE'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='visitType'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('visitType')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {visitTypes &&
                                    visitTypes.map((visitType, index) => (
                                      <option key={index} value={visitType}>
                                        {visitType}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>
                          )}

                          {contract ? (
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                              value={`${
                                contract.serviceFrequencyCode === FREQUENCY.OTHER
                                  ? contract.serviceFrequency
                                  : intl.formatMessage({
                                      id: `DROPDOWN.FREQUENCY.${contract.serviceFrequencyCode}`,
                                    })
                              }`}
                            ></CustomInputRenderer>
                          ) : (
                            <>
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='frequencyCode'
                                object={
                                  <select
                                    className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                    {...formik.getFieldProps('frequencyCode')}
                                    onChange={(e) => {
                                      if (e.target.value === FREQUENCY.OTHER) {
                                        formik.setFieldValue('frequency', '')
                                      }
                                      formik.handleChange(e)
                                    }}
                                    disabled={formik.isSubmitting}
                                  >
                                    <option value=''>
                                      {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                    </option>
                                    {FREQUENCIES.map((frequency, index) => (
                                      <option key={index} value={frequency}>
                                        {intl.formatMessage({
                                          id: `DROPDOWN.FREQUENCY.${frequency}`,
                                        })}
                                      </option>
                                    ))}
                                  </select>
                                }
                              ></CustomInputRenderer>

                              {formik.values.frequencyCode === FREQUENCY.OTHER && (
                                <CustomInputRenderer
                                  placeholder={intl.formatMessage({id: 'UTIL.SPECIFY'})}
                                  disabled={formik.isSubmitting}
                                  formik={formik}
                                  fieldName={`frequency`}
                                  enableInputLength={true}
                                ></CustomInputRenderer>
                              )}
                            </>
                          )}

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CATEGORY'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='category'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('category')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {serviceCategories &&
                                  serviceCategories.map((category, index) => (
                                    <option key={index} value={category}>
                                      {category}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='countryCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('countryCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {COUNTRIES_EN &&
                                  COUNTRIES_EN.map((country, index) => (
                                    <option key={index} value={country.code}>
                                      {intl.formatMessage({id: `DROPDOWN.COUNTRY.${country.code}`})}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='address'
                            enableInputLength={true}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <div className='row mb-5'>
                          <ContactEditor
                            formik={formik}
                            fieldName='contacts'
                            disabled={formik.isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
                    <ContactEditor
                      formik={formik}
                      fieldName='technicians'
                      disabled={formik.isSubmitting}
                      primaryLabel={intl.formatMessage({id: 'UTIL.PRIMARY_TECHNICIAN'})}
                    />
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_4' role='tabpanel'>
                    <div className='row mb-5'>
                      {reportMetadata && reportMetadata.metadata && (
                        <ReportRenderer
                          formik={formik}
                          path={`metadata.items`}
                          parameters={{FINAL_VERSION: get(formik.values, `finalVersion`)}}
                        />
                      )}
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_5' role='tabpanel'>
                    <div className='row mb-5'>
                      <FilePicker
                        disabled={formik.isSubmitting}
                        formik={formik}
                        fieldName='attachments'
                        deleteConfirmationModalId='report-new-attachments-delete-confirmation-modal'
                        setIsAttachmentProcessing={setIsAttachmentProcessing}
                      />
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_6' role='tabpanel'>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'REPORT.WORK_ORDER_REQUEST.DETAILS.HEADER'})}
                      />

                      {!get(formik.values, `workOrderRequest.lines`) ||
                        (get(formik.values, `workOrderRequest.lines`).length === 0 && (
                          <CustomInputRenderer
                            object={
                              <span className='fs-8 text-danger'>
                                {intl.formatMessage({
                                  id: 'REPORT.WORK_ORDER_REQUEST.DETAILS.MESSAGE.LABEL',
                                })}
                              </span>
                            }
                          ></CustomInputRenderer>
                        ))}

                      <FieldArray name='details'>
                        {() => (
                          <>
                            {get(formik.values, `workOrderRequest.lines`) &&
                              get(formik.values, `workOrderRequest.lines`).map((line, index) => (
                                <div className='row mx-0 px-0' key={index}>
                                  <div className='col-xxl-3 my-2'>
                                    <div className='d-flex justify-content-end align-items-center'>
                                      <span className='text-white'>
                                        <span
                                          className='border badge bg-light-secondary cursor-pointer'
                                          style={{zIndex: '10'}}
                                          title={intl.formatMessage({id: 'UTIL.REMOVE'})}
                                          onClick={() =>
                                            !formik.isSubmitting && removeDetail(index)
                                          }
                                        >
                                          <div className='d-flex flex-row justify-content-start align-items-center'>
                                            <i
                                              className={classNames({
                                                'bi bi-x fs-2 text-danger': !formik.isSubmitting,
                                                'bi bi-x fs-2 text-secondary': formik.isSubmitting,
                                              })}
                                            />
                                          </div>
                                        </span>
                                      </span>
                                    </div>
                                  </div>

                                  <div className='col-xxl-9'>
                                    <div className='row'>
                                      <div className='col-xxl-4 my-1'>
                                        <select
                                          className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                          {...formik.getFieldProps(
                                            `workOrderRequest.lines.${index}.workOrderTypeCode`
                                          )}
                                          onChange={(e) => formik.handleChange(e)}
                                          disabled={formik.isSubmitting}
                                        >
                                          <option value=''>
                                            {intl.formatMessage({
                                              id: 'UTIL.DROPDOWN.DEFAULT',
                                            })}
                                          </option>
                                          <option value={IWorkOrderLineType.SPARE_PART}>
                                            {intl.formatMessage({
                                              id: `${`WORK_ORDER_LINE_TYPE.${IWorkOrderLineType.SPARE_PART}`}`,
                                            })}
                                          </option>
                                          <option value={IWorkOrderLineType.MAINTENANCE}>
                                            {intl.formatMessage({
                                              id: `${`WORK_ORDER_LINE_TYPE.${IWorkOrderLineType.MAINTENANCE}`}`,
                                            })}
                                          </option>
                                          <option value={IWorkOrderLineType.OTHER}>
                                            {intl.formatMessage({
                                              id: `${`WORK_ORDER_LINE_TYPE.${IWorkOrderLineType.OTHER}`}`,
                                            })}
                                          </option>
                                        </select>
                                        <div className='d-flex flex-row p-1'>
                                          <div className='d-flex flex-column flex-row-fluid'>
                                            <div className='d-flex flex-row flex-column-fluid'>
                                              <div className='d-flex flex-row-fluid flex-start'>
                                                {get(
                                                  formik.touched,
                                                  `workOrderRequest.lines.${index}.details`
                                                ) &&
                                                  get(
                                                    formik.errors,
                                                    `workOrderRequest.lines.${index}.details`
                                                  ) && (
                                                    <span className='fs-8 text-danger'>
                                                      {get(
                                                        formik.errors,
                                                        `workOrderRequest.lines.${index}.details`
                                                      )?.toUpperCase() ?? ''}
                                                    </span>
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      <div className='col-xxl-8 my-1'>
                                        <input
                                          type='text'
                                          className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
                                          placeholder={intl.formatMessage({
                                            id: 'UTIL.DETAILS',
                                          })}
                                          {...formik.getFieldProps(
                                            `workOrderRequest.lines.${index}.details`
                                          )}
                                          onChange={(e) =>
                                            formik.setFieldValue(
                                              `workOrderRequest.lines.${index}.details`,
                                              e.target.value.toUpperCase()
                                            )
                                          }
                                          disabled={formik.isSubmitting}
                                        />
                                        <div className='d-flex flex-row p-1'>
                                          <div className='d-flex flex-column flex-row-fluid'>
                                            <div className='d-flex flex-row flex-column-fluid'>
                                              <div className='d-flex flex-row-fluid flex-start'>
                                                {get(
                                                  formik.touched,
                                                  `workOrderRequest.lines.${index}.details`
                                                ) &&
                                                  get(
                                                    formik.errors,
                                                    `workOrderRequest.lines.${index}.details`
                                                  ) && (
                                                    <span className='fs-8 text-danger'>
                                                      {get(
                                                        formik.errors,
                                                        `workOrderRequest.lines.${index}.details`
                                                      )?.toUpperCase() ?? ''}
                                                    </span>
                                                  )}
                                              </div>

                                              {formik &&
                                                get(
                                                  formik.values,
                                                  `workOrderRequest.lines.${index}.details`
                                                )?.length > 0 && (
                                                  <div className='d-flex flex-row-auto flex-end align-items-start'>
                                                    <span className='fs-8 text-muted'>{`${
                                                      get(
                                                        formik.values,
                                                        `workOrderRequest.lines.${index}.details`
                                                      )?.length || 0
                                                    }/255`}</span>
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            <div className='row mx-0 px-0 mt-2'>
                              <div className='col-xxl-3'>
                                <div className='d-flex justify-content-end align-items-center mb-3'>
                                  <span
                                    className='border badge bg-light-secondary cursor-pointer'
                                    style={{zIndex: '10'}}
                                    title={intl.formatMessage({id: 'UTIL.ADD'})}
                                    onClick={() => !formik.isSubmitting && addDetail()}
                                  >
                                    <div className='d-flex flex-row justify-content-start align-items-center'>
                                      <i
                                        className={classNames({
                                          'bi bi-plus fs-2 text-success': !formik.isSubmitting,
                                          'bi bi-plus fs-2 text-secondary': formik.isSubmitting,
                                        })}
                                      />
                                    </div>
                                  </span>
                                </div>
                              </div>
                              <div className='col-xxl-9'></div>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>

                    <div className='row mb-5'>
                      <CustomInputRenderer
                        label={intl.formatMessage({id: 'UTIL.PRIORITY_LEVEL'})}
                        disabled={formik.isSubmitting}
                        formik={formik}
                        fieldName='workOrderRequest.priorityCode'
                        object={
                          <select
                            className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                            {...formik.getFieldProps('workOrderRequest.priorityCode')}
                            onChange={(e) => formik.handleChange(e)}
                            disabled={formik.isSubmitting}
                            title={intl.formatMessage({
                              id: 'TICKET.IMPACT_AND_PRIORITY.REQUIRED',
                            })}
                          >
                            <option value=''>
                              {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                            </option>
                            {PriorityLevelOptions &&
                              PriorityLevelOptions.map((priorityLevel, index) => (
                                <option key={index} value={priorityLevel}>
                                  {intl.formatMessage({id: `TICKET.PRIORITY.${priorityLevel}`})}
                                </option>
                              ))}
                          </select>
                        }
                      ></CustomInputRenderer>
                      <CustomInputRenderer
                        label={intl.formatMessage({id: 'REPORT.WORK_ORDER_REQUEST.NOTES.LABEL'})}
                        disabled={formik.isSubmitting}
                        formik={formik}
                        fieldName='workOrderRequest.observations'
                        object={
                          <textarea
                            rows={4}
                            className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                            placeholder={intl.formatMessage({
                              id: 'REPORT.WORK_ORDER_REQUEST.NOTES.PLACEHOLDER',
                            })}
                            {...formik.getFieldProps('workOrderRequest.observations')}
                            onChange={(e) =>
                              formik.setFieldValue(
                                'workOrderRequest.observations',
                                e.target.value.toUpperCase()
                              )
                            }
                            disabled={formik.isSubmitting}
                          />
                        }
                        enableInputLength={true}
                        inputLength={3000}
                      ></CustomInputRenderer>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({
                          id: 'UTIL.ATTACHMENTS',
                        })}
                      />
                      <FilePicker
                        disabled={formik.isSubmitting}
                        formik={formik}
                        fieldName='workOrderRequest.attachments'
                        deleteConfirmationModalId='report-new-work-order-attachments-delete-confirmation-modal'
                        setIsAttachmentProcessing={setIsAttachmentProcessing}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

export {ReportNewPage}
